import { z } from 'zod';
import {
  ServerAppModel,
  ServerHeoModel,
  zServerAppModel,
  zServerHeoModel,
} from './employee-2024.types';
import {
  ServerAppModel2026,
  ServerHeoModel2026,
  zServerAppModel2026,
  zServerHeoModel2026,
} from './employee-2026.types';

/**
 * `ExtendedEmployeeCardDto.helios` is typed as `any` by NSwag, and it ships in
 * two different shapes at the same time:
 *
 * 1. NEW (since BE commit 7f8ed603 `feat: store helios data in same format as
 *    employee card`) - a full `EmployeeCardData` snapshot, i.e. the very same
 *    envelope as `EmployeeCardHistory.data`:
 *
 *        { karta: {...}, app: {...}, dodatecneAtributy: {...}, version: "2024.1" | "2026.1" }
 *
 * 2. LEGACY - the raw Helios `karta` object at the root (flat, no envelope).
 *    Rows written before the BE change keep this shape until the next
 *    `editusercard` / `createusercard` ETL push arrives for that employee, so
 *    both shapes must be supported simultaneously and indefinitely.
 *
 * Detection cannot be delegated to zod: `zServerHeoModel` / `zServerAppModel`
 * are non-strict objects whose every member is `.nullish()`, so feeding the new
 * envelope to `zServerHeoModel.parse()` SUCCEEDS and returns `{}` - every
 * unknown key is silently stripped. The shape therefore has to be decided
 * structurally, before any schema is applied.
 */

export type HeliosSnapshotVersion = '2024.1' | '2026.1';

export type HeliosSnapshotInvalidReason =
  /** payload is a primitive or an array */
  | 'not-an-object'
  /** `Karta` + `App` at the root - a non-camelCased snapshot (EmployeeCardListDto) */
  | 'pascal-case-envelope'
  /** neither a usable envelope nor anything resembling a Helios `karta` */
  | 'unrecognized-shape';

export type HeliosSnapshotKind =
  | 'v2024'
  | 'v2026'
  | 'legacy-karta'
  | 'empty'
  | 'invalid';

interface HeliosSnapshotBase {
  kind: HeliosSnapshotKind;
  /** `null` for legacy / empty / invalid payloads - they carry no version tag. */
  version: HeliosSnapshotVersion | null;
  /**
   * Non-fatal schema problems collected while parsing `karta` / `app`.
   * A half that failed to parse is returned as `undefined`, the other half is
   * kept - never blank the whole comparison column because of one bad field.
   */
  issues: z.ZodIssue[];
}

export interface HeliosSnapshotV2024 extends HeliosSnapshotBase {
  kind: 'v2024';
  version: '2024.1';
  karta: ServerHeoModel | undefined;
  app: ServerAppModel | undefined;
  dodatecneAtributy: unknown;
}

export interface HeliosSnapshotV2026 extends HeliosSnapshotBase {
  kind: 'v2026';
  version: '2026.1';
  karta: ServerHeoModel2026 | undefined;
  app: ServerAppModel2026 | undefined;
  dodatecneAtributy: unknown;
}

export interface HeliosSnapshotLegacyKarta extends HeliosSnapshotBase {
  kind: 'legacy-karta';
  version: null;
  karta: ServerHeoModel | undefined;
  app: undefined;
  dodatecneAtributy: undefined;
}

export interface HeliosSnapshotEmpty extends HeliosSnapshotBase {
  kind: 'empty';
  version: null;
  karta: undefined;
  app: undefined;
  dodatecneAtributy: undefined;
}

export interface HeliosSnapshotInvalid extends HeliosSnapshotBase {
  kind: 'invalid';
  version: null;
  karta: undefined;
  app: undefined;
  dodatecneAtributy: undefined;
  reason: HeliosSnapshotInvalidReason;
}

export type HeliosSnapshot =
  | HeliosSnapshotV2024
  | HeliosSnapshotV2026
  | HeliosSnapshotLegacyKarta
  | HeliosSnapshotEmpty
  | HeliosSnapshotInvalid;

/**
 * Structure of the new envelope only - the halves are validated separately so
 * that one malformed field cannot reject the whole snapshot.
 *
 * `karta` / `app` use `.nullable()` and NOT `.nullish()` on purpose: the key
 * must be PRESENT for the payload to count as an envelope.
 */
const zHeliosSnapshotEnvelope = z.object({
  version: z.string().nullish(),
  karta: z.record(z.unknown()).nullable(),
  app: z.record(z.unknown()).nullable(),
  dodatecneAtributy: z.unknown().optional(),
});

/**
 * Same schema with every top-level member wrapped in `.catch(undefined)`, used
 * as a salvage pass: one unexpected value type degrades that single top-level
 * field to `undefined` instead of discarding the whole half.
 *
 * The salvage is deliberately shallow - a bad leaf inside a nested section
 * (e.g. `zdravotniPojistovnaAStav`) drops that whole section. The mappers
 * tolerate a missing section, so the worst case is a few blank rows, never a
 * crash or a blank column.
 */
const toLenientSchema = (schema: z.ZodObject<z.ZodRawShape>): z.ZodTypeAny => {
  const shape: z.ZodRawShape = {};
  const entries = Object.entries(schema.shape);
  for (const entry of entries) {
    shape[entry[0]] = (entry[1] as z.ZodTypeAny).catch(undefined as never);
  }
  return z.object(shape);
};

const zServerHeoModelLenient = toLenientSchema(zServerHeoModel);
const zServerAppModelLenient = toLenientSchema(zServerAppModel);
const zServerHeoModel2026Lenient = toLenientSchema(zServerHeoModel2026);
const zServerAppModel2026Lenient = toLenientSchema(zServerAppModel2026);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Root keys of a legacy snapshot must overlap the flat Helios `karta` model. */
const LEGACY_KARTA_KEYS: string[] = Object.keys(zServerHeoModel.shape);

const looksLikeLegacyKarta = (value: Record<string, unknown>): boolean =>
  Object.keys(value).some((key) => LEGACY_KARTA_KEYS.indexOf(key) !== -1);

interface ParsedPart<T> {
  value: T | undefined;
  issues: z.ZodIssue[];
}

const parsePart = <T>(
  strict: z.ZodTypeAny,
  lenient: z.ZodTypeAny,
  value: unknown
): ParsedPart<T> => {
  if (value === null || value === undefined) return { value: undefined, issues: [] };

  const result = strict.safeParse(value);
  if (result.success) return { value: result.data as T, issues: [] };

  const salvaged = lenient.safeParse(value);
  return {
    value: salvaged.success ? (salvaged.data as T) : undefined,
    issues: result.error.issues,
  };
};

const emptySnapshot = (): HeliosSnapshotEmpty => ({
  kind: 'empty',
  version: null,
  karta: undefined,
  app: undefined,
  dodatecneAtributy: undefined,
  issues: [],
});

const invalidSnapshot = (
  reason: HeliosSnapshotInvalidReason,
  issues: z.ZodIssue[] = []
): HeliosSnapshotInvalid => ({
  kind: 'invalid',
  version: null,
  karta: undefined,
  app: undefined,
  dodatecneAtributy: undefined,
  reason,
  issues,
});

const parseLegacyKarta = (
  payload: Record<string, unknown>,
  issues: z.ZodIssue[]
): HeliosSnapshot => {
  if (!looksLikeLegacyKarta(payload)) {
    return invalidSnapshot('unrecognized-shape', issues);
  }

  const parsedKarta = parsePart<ServerHeoModel>(
    zServerHeoModel,
    zServerHeoModelLenient,
    payload
  );
  return {
    kind: 'legacy-karta',
    version: null,
    karta: parsedKarta.value,
    app: undefined,
    dodatecneAtributy: undefined,
    issues: issues.concat(parsedKarta.issues),
  };
};

/**
 * Decide which shape `ExtendedEmployeeCardDto.helios` is in, validate it and
 * hand back the pieces `mapToApp2024` / `mapToApp2026` expect.
 *
 * Never throws. Callers switch on `kind` and should log `kind === 'invalid'`
 * explicitly - `not-an-object` / `pascal-case-envelope` carry no `issues`:
 *
 *   const snapshot = parseHeliosSnapshot(result.helios);
 *   switch (snapshot.kind) {
 *     case 'v2026':
 *       return mapToApp2026(snapshot.karta, snapshot.app, snapshot.dodatecneAtributy, false);
 *     case 'v2024':
 *     case 'legacy-karta':
 *       return mapToApp2024To2026(
 *         mapToApp2024(snapshot.karta, snapshot.app, snapshot.dodatecneAtributy, false)
 *       );
 *     default:
 *       return null;
 *   }
 */
export const parseHeliosSnapshot = (payload: unknown): HeliosSnapshot => {
  if (payload === null || payload === undefined) return emptySnapshot();
  if (!isPlainObject(payload)) return invalidSnapshot('not-an-object');
  if (Object.keys(payload).length === 0) return emptySnapshot();

  // Not camelCased by the BE DTO (EmployeeCardListDto still exposes the raw
  // JObject). Detect it explicitly so it fails loudly instead of parsing to {}.
  if ('Karta' in payload && 'App' in payload) {
    return invalidSnapshot('pascal-case-envelope');
  }

  // The new envelope, detected on key presence only. Extra root keys must not
  // disqualify it - falling through to `invalid` would blank the whole column.
  if ('karta' in payload && 'app' in payload) {
    const envelope = zHeliosSnapshotEnvelope.safeParse(payload);

    if (envelope.success) {
      const { version, karta, app, dodatecneAtributy } = envelope.data;
      const extras = dodatecneAtributy === null ? undefined : dodatecneAtributy;

      if (version === '2026.1') {
        const parsedKarta = parsePart<ServerHeoModel2026>(
          zServerHeoModel2026,
          zServerHeoModel2026Lenient,
          karta
        );
        const parsedApp = parsePart<ServerAppModel2026>(
          zServerAppModel2026,
          zServerAppModel2026Lenient,
          app
        );
        return {
          kind: 'v2026',
          version: '2026.1',
          karta: parsedKarta.value,
          app: parsedApp.value,
          dodatecneAtributy: extras,
          issues: parsedKarta.issues.concat(parsedApp.issues),
        };
      }

      // BE reads an unknown / missing `version` as 2024.1
      // (EmployeeCardDataConverter) - mirror that here instead of failing.
      const parsedKarta = parsePart<ServerHeoModel>(
        zServerHeoModel,
        zServerHeoModelLenient,
        karta
      );
      const parsedApp = parsePart<ServerAppModel>(
        zServerAppModel,
        zServerAppModelLenient,
        app
      );
      return {
        kind: 'v2024',
        version: '2024.1',
        karta: parsedKarta.value,
        app: parsedApp.value,
        dodatecneAtributy: extras,
        issues: parsedKarta.issues.concat(parsedApp.issues),
      };
    }

    // `karta` / `app` are present but are not objects. Do not give up - a legacy
    // Helios `karta` that happens to carry such keys is still readable.
    return parseLegacyKarta(payload, envelope.error.issues);
  }

  return parseLegacyKarta(payload, []);
};
